import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignJobInput, JobDetail, JobEvent, JobListItem, JobStatus, RecordPaymentInput, UpdateJobInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useJobs(workspaceId: string | null, params: { status?: string; locationId?: string }) {
  return useQuery({
    queryKey: [workspaceId, 'jobs', params],
    queryFn: () => api.get<JobListItem[]>(workspacePath(workspaceId!, '/jobs'), { query: params }),
    enabled: !!workspaceId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useJob(workspaceId: string | null, jobId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'jobs', jobId],
    queryFn: () => api.get<JobDetail>(workspacePath(workspaceId!, `/jobs/${jobId}`)),
    enabled: !!workspaceId && !!jobId,
    staleTime: 10_000,
  });
}

export function useJobEvents(workspaceId: string | null, jobId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'jobs', jobId, 'events'],
    queryFn: () => api.get<JobEvent[]>(workspacePath(workspaceId!, `/jobs/${jobId}/events`)),
    enabled: !!workspaceId && !!jobId,
  });
}

function useInvalidateJob(workspaceId: string | null, jobId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'jobs'] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'jobs', jobId] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'reports'] });
  };
}

export function useUpdateJob(workspaceId: string | null, jobId: string) {
  const invalidate = useInvalidateJob(workspaceId, jobId);
  return useMutation({ mutationFn: (input: UpdateJobInput) => api.patch(workspacePath(workspaceId!, `/jobs/${jobId}`), input), onSuccess: invalidate });
}

export function useUpdateJobStatus(workspaceId: string | null, jobId: string) {
  const invalidate = useInvalidateJob(workspaceId, jobId);
  return useMutation({ mutationFn: (to: JobStatus) => api.post(workspacePath(workspaceId!, `/jobs/${jobId}/status`), { to }), onSuccess: invalidate });
}

export function useAssignJob(workspaceId: string | null, jobId: string) {
  const invalidate = useInvalidateJob(workspaceId, jobId);
  return useMutation({ mutationFn: (input: AssignJobInput) => api.post(workspacePath(workspaceId!, `/jobs/${jobId}/assign`), input), onSuccess: invalidate });
}

export function useRecordPayment(workspaceId: string | null, jobId: string) {
  const invalidate = useInvalidateJob(workspaceId, jobId);
  return useMutation({ mutationFn: (input: RecordPaymentInput) => api.post(workspacePath(workspaceId!, `/jobs/${jobId}/payments`), input), onSuccess: invalidate });
}
